import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Select, Tooltip } from 'antd';
import { Bar } from 'react-chartjs-2';
import { WarningOutlined } from '@ant-design/icons';
import colors from 'Styles/Colors';
import moment from 'moment';

const { Option } = Select;

const InventoryChart = ({ data }) => {
  const inventory = data?.inventorySummary || [];
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Set default product on data load
  useEffect(() => {
    if (inventory.length > 0 && !selectedProductId) {
      const defaultProduct = inventory[0];
      setSelectedProductId(defaultProduct.id);
      setSelectedProduct(defaultProduct);
    }
  }, [inventory, selectedProductId]);

  // Handle product selection
  const handleProductChange = productId => {
    const product = inventory.find(p => p.id === productId);
    setSelectedProductId(productId);
    setSelectedProduct(product);
  };

  if (inventory.length === 0) {
    return (
      <InventoryContainer>
        <Card title="Inventory Overview" className="dashboard-card full-width">
          <div className="no-inventory">
            <div className="no-inventory-text">No inventory data available</div>
          </div>
        </Card>
      </InventoryContainer>
    );
  }

  if (!selectedProduct) {
    return (
      <InventoryContainer>
        <Card title="Inventory Overview" className="dashboard-card full-width">
          <div className="loading-inventory">Loading inventory data...</div>
        </Card>
      </InventoryContainer>
    );
  }

  // Process daily inventory data
  const dailyData = selectedProduct.dailyInventory || [];
  const dates = dailyData.map(day => moment(day.date).format('MM/DD'));
  const availableData = dailyData.map(day => day.available);
  const usedData = dailyData.map(day => day.used);
  const assignedData = dailyData.map(day => day.assigned);

  // Check for overbooking (used > stockQty)
  const overbookingDates = dailyData.filter(day => day.used > selectedProduct.stockQty);

  const chartData = {
    labels: dates,
    datasets: [
      {
        label: 'Available',
        data: availableData,
        backgroundColor: '#10AC84',
        borderColor: '#10AC84',
        borderWidth: 1
      },
      {
        label: 'Reserved',
        data: usedData,
        backgroundColor: '#2875c3',
        borderColor: '#2875c3',
        borderWidth: 1
      },
      {
        label: 'Assigned',
        data: assignedData,
        backgroundColor: '#ff9f43',
        borderColor: '#ff9f43',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            const dateIndex = context[0].dataIndex;
            return moment(dailyData[dateIndex].date).format('MMMM DD, YYYY');
          },
          afterLabel: function(context) {
            const dateIndex = context.dataIndex;
            const dayData = dailyData[dateIndex];
            if (dayData.used > selectedProduct.stockQty) {
              return '⚠️ Overbooking detected!';
            }
            return '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: colors.border.primary + '50'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <InventoryContainer>
      <Card title="Inventory Overview" className="dashboard-card full-width">
        <div className="inventory-header">
          <div className="product-selector">
            <label>Select Product:</label>
            <Select value={selectedProductId} onChange={handleProductChange} style={{ width: 300 }} placeholder="Select a product">
              {inventory.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.name} ({product.type.toUpperCase()})
                </Option>
              ))}
            </Select>
          </div>

          <div className="product-info">
            <div className="product-details">
              <span className="product-name">{selectedProduct.name}</span>
              <span className="product-id">ID: {selectedProduct.id}</span>
            </div>
            <div className="product-stats">
              <div className="stat">
                <span className="stat-label">Initial Quantity:</span>
                <span className="stat-value">{selectedProduct.stockQty}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Type:</span>
                <span className="stat-value product-type">{selectedProduct.type === 'stall' ? 'Stall Product' : 'RV Product'}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Flip:</span>
                <span className={`stat-value flip-status ${selectedProduct.flip ? 'enabled' : 'disabled'}`}>
                  {selectedProduct.flip ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {overbookingDates.length > 0 && (
          <div className="overbooking-warning">
            <WarningOutlined />
            <span>
              Overbooking detected on {overbookingDates.length} day{overbookingDates.length > 1 ? 's' : ''}. Hover over the chart to see details.
            </span>
          </div>
        )}

        <div className="chart-container">
          <Bar data={chartData} options={chartOptions} />
        </div>

        <div className="chart-legend-custom">
          <div className="legend-item">
            <div className="legend-color available"></div>
            <span>Available - Total spots available for booking</span>
          </div>
          <div className="legend-item">
            <div className="legend-color used"></div>
            <span>Reserved - Spots that have been booked</span>
          </div>
          <div className="legend-item">
            <div className="legend-color assigned"></div>
            <span>Assigned - Spots that have been assigned to specific locations</span>
          </div>
        </div>

        {overbookingDates.length > 0 && (
          <div className="overbooking-details">
            <div className="overbooking-header">Overbooking Details:</div>
            <div className="overbooking-list">
              {overbookingDates.map((day, index) => (
                <div key={index} className="overbooking-item">
                  <span className="date">{moment(day.date).format('MM/DD/YYYY')}:</span>
                  <span className="details">
                    {day.used} reserved / {selectedProduct.stockQty} total stock
                    <Tooltip title="Overbooking. Please add more spaces to this product or move renters to other available products. In the worst case, cancel the extra orders.">
                      <WarningOutlined className="warning-icon" />
                    </Tooltip>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </InventoryContainer>
  );
};

const InventoryContainer = styled.div`
  width: 100%;

  .dashboard-card {
    height: 100%;

    &.full-width {
      width: 100%;
    }

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
    }

    .ant-card-body {
      padding: 20px;
    }
  }

  .inventory-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .product-selector {
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: ${colors.text.primary};
    }
  }

  .product-info {
    text-align: right;

    .product-details {
      margin-bottom: 8px;

      .product-name {
        font-size: 18px;
        font-weight: 600;
        color: ${colors.text.primary};
        margin-right: 8px;
      }

      .product-id {
        font-size: 12px;
        color: ${colors.text.secondary};
      }
    }

    .product-stats {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;

      .stat {
        display: flex;
        flex-direction: column;
        text-align: right;

        .stat-label {
          font-size: 11px;
          color: ${colors.text.secondary};
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .stat-value {
          font-size: 14px;
          font-weight: 500;
          color: ${colors.text.primary};
          height: 25px;
          display: flex;
          align-items: center;
          justify-content: center;

          &.product-type {
            padding: 2px 8px;
            background: #fff;
            color: #11181f;
            border-radius: 4px;
            font-size: 12px;
            border: 1px solid #11181f;
          }

          &.flip-status {
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;

            &.enabled {
              background: #10ac8420;
              color: #10ac84;
              border: 1px solid #10ac8440;
            }

            &.disabled {
              background: #ee525320;
              color: #ee5253;
              border: 1px solid #ee525340;
            }
          }
        }
      }
    }
  }

  .overbooking-warning {
    background: ${colors.error.primary}10;
    border: 1px solid ${colors.error.primary}30;
    color: ${colors.error.primary};
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
  }

  .chart-container {
    height: 400px;
    margin-bottom: 20px;
  }

  .chart-legend-custom {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;
    margin-bottom: 16px;

    .legend-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      font-size: 12px;
      color: ${colors.text.secondary};

      &:last-child {
        margin-bottom: 0;
      }

      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        margin-right: 8px;

        &.available {
          background-color: #10ac84;
        }
        &.used {
          background-color: #2875c3;
        }
        &.assigned {
          background-color: #ff9f43;
        }
      }
    }
  }

  .overbooking-details {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;

    .overbooking-header {
      font-size: 14px;
      font-weight: 600;
      color: ${colors.error.primary};
      margin-bottom: 12px;
    }

    .overbooking-list {
      .overbooking-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid ${colors.border.primary}30;

        &:last-child {
          border-bottom: none;
        }

        .date {
          font-weight: 500;
          color: ${colors.text.primary};
        }

        .details {
          color: ${colors.error.primary};
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;

          .warning-icon {
            cursor: help;
          }
        }
      }
    }
  }

  .no-inventory,
  .loading-inventory {
    text-align: center;
    padding: 60px 20px;

    .no-inventory-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .no-inventory-text {
      font-size: 16px;
      color: ${colors.text.secondary};
    }
  }

  .loading-inventory {
    font-size: 16px;
    color: ${colors.text.secondary};
  }

  @media (max-width: 768px) {
    .inventory-header {
      flex-direction: column;

      .product-info {
        text-align: left;

        .product-stats {
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 12px;

          .stat {
            text-align: left;
          }
        }
      }
    }

    .product-selector {
      .ant-select {
        width: 100% !important;
      }
    }

    .chart-container {
      height: 300px;
    }

    .overbooking-details {
      .overbooking-list {
        .overbooking-item {
          flex-direction: column;
          align-items: flex-start;

          .details {
            margin-top: 4px;
          }
        }
      }
    }
  }
`;

export default InventoryChart;
